import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getPendingInvitations,
  getUsersSummary,
  listUsers,
  type UsersListParams,
} from '@/requests/users'

/** Fetches Active/Deactivated member counts for the Users page TabBar. */
export function useUsersSummaryQuery() {
  return useQuery({
    queryKey: ['users', 'summary'],
    queryFn: getUsersSummary,
    staleTime: 60_000,
  })
}

export function useUsersQuery(options: UsersListParams = {}) {
  const [pageIndex, setPageIndex] = useState(0)

  const query = useInfiniteQuery({
    queryKey: ['users', options],
    queryFn: ({ pageParam }) =>
      listUsers({ ...options, cursor: pageParam ?? options.cursor }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.nextCursor ?? undefined,
  })

  const totalPages = query.data?.pages.length ?? 1
  const currentPageData = query.data?.pages[pageIndex] ?? null
  const isOnLastFetchedPage = pageIndex === totalPages - 1
  const hasNextPage = isOnLastFetchedPage ? (query.hasNextPage ?? false) : true

  function goToNextPage() {
    if (isOnLastFetchedPage) {
      void query.fetchNextPage()
    }
    setPageIndex((previous) => previous + 1)
  }

  function goToPrevPage() {
    setPageIndex((previous) => Math.max(0, previous - 1))
  }

  return {
    ...query,
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  }
}

export function usePendingInvitationsQuery() {
  return useQuery({
    queryKey: ['users', 'invitations'],
    queryFn: getPendingInvitations,
    staleTime: 60_000,
  })
}
